/**
 * The works orders behind one week, split into what it is CLEARING and what is DUE to start
 * in it. Two lists rather than one, because they answer different questions: the left one is
 * promises already broken, the right one is promises still keepable.
 *
 * The split is the drill's own, not a re-derivation: `is_arrears` means the operation was
 * scheduled EARLIER than the bucket now working it. A section with no hours is omitted
 * entirely — an empty "clearing backlog" heading reads as a finding when it is the opposite.
 */

import React from 'react'
import type { LoadDrillRow } from '../../types/loading'
import { byWorksOrder, type WoLine } from '../../viewmodels/loadingWo'
import { hrs } from './format'

const ROW = 'grid grid-cols-[74px_1fr_66px_126px_58px] items-center gap-x-2.5 border-t py-1.5'
const MONO = 'font-mono text-[10.5px]'

const LISTS = 'grid items-start gap-x-[26px] px-4 pb-3.5 pt-1'
/** Side by side while the reader scans, stacked while a route is open — see WeekSections. */
const COLS: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-[repeat(auto-fit,minmax(420px,1fr))]',
}

const tagOf = (line: WoLine, arrears: boolean): 'OVERDUE' | 'ARREARS' | 'ON PLAN' =>
  line.isOverdue ? 'OVERDUE' : arrears ? 'ARREARS' : 'ON PLAN'

const TAG_STYLE: Record<string, { background: string; color: string }> = {
  OVERDUE: { background: 'var(--lc-red-bg)', color: 'var(--lc-red-ink)' },
  ARREARS: { background: 'var(--lc-amber-bg)', color: 'var(--lc-amber-ink)' },
  'ON PLAN': { background: 'var(--lc-blue-soft)', color: 'var(--lc-blue)' },
}

const day = (iso: string | null): string =>
  iso === null ? '—' : new Date(`${iso}T00:00:00`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })

function Row({ line, arrears, open, onOpen }: {
  line: WoLine
  arrears: boolean
  open: boolean
  onOpen: (wo: string) => void
}): React.JSX.Element {
  const tag = tagOf(line, arrears)
  return (
    <div className={ROW} style={{ borderColor: 'var(--lc-b07)' }}>
      <button
        type="button"
        aria-pressed={open}
        title={`Open the route for works order ${line.woKey} — every operation, machine and date`}
        onClick={() => onOpen(line.woKey)}
        className="text-left font-mono text-[11px] font-semibold underline decoration-dotted underline-offset-2"
        style={{ color: open ? 'var(--lc-blue)' : 'var(--lc-ink)' }}
      >
        {line.woKey}
      </button>
      <span className={`${MONO} overflow-hidden text-ellipsis whitespace-nowrap`} style={{ color: 'var(--lc-ink4)' }}>{line.part ?? '—'}</span>
      {/* Every operation this job has in this week, named. The route below dates them. */}
      <span className={`${MONO} overflow-hidden text-ellipsis whitespace-nowrap`} style={{ color: 'var(--lc-faint)' }}>
        {line.ops.join(', ')}
      </span>
      <span
        className="whitespace-nowrap rounded-[3px] px-[5px] py-[2px] text-center font-mono text-[8.5px] font-semibold tracking-[.05em]"
        style={TAG_STYLE[tag]}
      >
        {`${tag} · ${day(line.requiredDate)}`}
      </span>
      <span
        className="text-right font-mono text-[11px] font-semibold tabular-nums"
        style={{ color: tag === 'ON PLAN' ? 'var(--lc-ink)' : 'var(--lc-red)' }}
      >
        {hrs(line.hours)}
      </span>
    </div>
  )
}

function Section({ label, tone, rows, arrears, total, limit, wo, onWo, detail }: {
  label: string
  tone: string
  rows: LoadDrillRow[]
  /** Which section this is — every row in it shares the flag, so the tag reads from here. */
  arrears: boolean
  total: number
  limit: number
  wo: string | null
  onWo: (wo: string) => void
  detail: (woKey: string) => React.ReactNode
}): React.JSX.Element | null {
  if (total <= 0 && !rows.length) return null
  const lines = byWorksOrder(rows)
  const shown = lines.slice(0, limit)
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2.5 pb-[5px] pt-3">
        <span className="font-mono text-[9px] font-semibold uppercase tracking-[.08em]" style={{ color: tone }}>{label}</span>
        <span className="font-mono text-[10px] font-medium" style={{ color: 'var(--lc-mut)' }}>{hrs(total)}</span>
      </div>
      {shown.map((line) => (
        <React.Fragment key={line.woKey}>
          <Row line={line} arrears={arrears} open={line.woKey === wo} onOpen={onWo} />
          {/* The route opens UNDER ITS OWN ROW. It used to be a card at the foot of the
              page, which meant the list and the thing it opened could not be read at the
              same time — and on a long list the row you clicked had scrolled away. */}
          {line.woKey === wo && (
            <div className="border-t pl-3" style={{ borderColor: 'var(--lc-b07)' }}>{detail(line.woKey)}</div>
          )}
        </React.Fragment>
      ))}
      <div className="border-t pt-[7px] font-sans text-[10px] leading-[1.4]" style={{ borderColor: 'var(--lc-b07)', color: 'var(--lc-faint)' }}>
        Showing {shown.length} of {lines.length} works orders ({rows.length} operations) ·
        Σ all rows = {hrs(total)}
      </div>
    </div>
  )
}

/**
 * ONE COLUMN WHILE A ROUTE IS OPEN (31 Aug 2026, Mike).
 *
 * A route is a wide object — every operation, its machine, and a date span drawn as a bar —
 * and inside a 420px column the bar is too short to read. It cannot simply span both columns:
 * a route opened from the RIGHT list would be drawn straight over the left one, because the
 * two lists are separate flows of one grid and neither makes room for the other. Stacking
 * them gives the route the full width and keeps it under the row that opened it, which is the
 * part that matters on an expanded list — a route at the foot of the panel is the layout this
 * replaced.
 */
export default function WeekSections({ rows, clearing, own, limit, wo, onWo, detail }: {
  rows: LoadDrillRow[]
  clearing: number
  own: number
  limit: number
  /** The works order whose route is open, so its number reads as selected. */
  wo: string | null
  onWo: (wo: string) => void
  /** The open works order's route, rendered under the FIRST row that names it — a works
   *  order can appear twice in a week (two operations), and it expands once. */
  detail: (woKey: string) => React.ReactNode
}): React.JSX.Element {
  const columns = wo !== null && rows.some((r) => r.wo_key === wo) ? 1 : 2
  return (
    <div data-columns={columns} className={`${LISTS} ${COLS[columns]}`}>
      <Section
        label="Clearing backlog · oldest arrears first"
        tone="var(--lc-red-ink)"
        rows={rows.filter((r) => r.is_arrears)}
        arrears
        total={clearing}
        limit={limit}
        wo={wo}
        onWo={onWo}
        detail={detail}
      />
      <Section
        label="Due to start this week"
        tone="var(--lc-teal-ink)"
        rows={rows.filter((r) => !r.is_arrears)}
        arrears={false}
        total={own}
        limit={limit}
        wo={wo}
        onWo={onWo}
        detail={detail}
      />
    </div>
  )
}
